<template>
  <div
    id="app"
    :class="[
      editorData.product,
      {
        rtl: showRtl,
        'modal-hidden': isModalOpen,
        iframe: editorData.inIframe,
      },
    ]"
  >
    <cert-modal
      :modal-options="modalOptions"
      :default-culture="this.seriesData.default_lang"
      :available-cultures="availableCultures"
      :modal-data="seriesData"
      v-if="modalOptions.active"
    ></cert-modal>
    <cert-uploader
      :editorData="editorData"
      :upload-options="uploadOptions"
      :modal-data="seriesData"
      v-if="uploadOptions.active"
    ></cert-uploader>

    <transition name="fade" mode="out-in">
      <cert-full-page-loader v-if="!loaded"></cert-full-page-loader>
      <cert-full-page-error
        v-else-if="loaded && onError.active"
        :message="onError.message"
      ></cert-full-page-error>
      <div class="flex-container start" v-else>
        <cert-form
          :current-culture="currentCulture"
          :form-data="editorData"
          :series-data="seriesData"
          :modal-options="modalOptions"
          :upload-options="uploadOptions"
        />
        <cert-template-viewer
          :available-cultures="availableCultures"
          :preview-options="editorData"
          :series-data="seriesData"
          :current-culture="currentCulture"
        />
      </div>
    </transition>
  </div>
</template>

<script>
import CertForm from "./components/CertForm";
import CertTemplateViewer from "./components/CertTemplateViewer";
import CertFullPageLoader from "./components/utilities/CertFullPageLoader";
import CertModal from "./components/utilities/CertModal";
import CertUploader from "./components/form/CertUploader.vue";
import CertFullPageError from "./components/utilities/CertFullPageError";
import supportedCultures from "./supportedCultures";
import axios from "axios";
import getUrlParameters from "./utilities/params";
import { inIframe, createNewGUID } from "./utilities/general";

export default {
  name: "App",
  data: function () {
    return {
      loaded: false,
      onError: { active: false, message: "" },
      showRtl: false,
      modalOptions: {
        active: false,
        editedValue: "",
        focusBackElem: "",
        inputLength: 0,
      },
      uploadOptions: {
        active: false,
        editedValue: "",
        focusBackElem: "",
      },

      currentCulture: { name: "", culture: "" },
      seriesData: {
        template_id: 1,
        certificate_template: {
          color: "red",
          resources: {},
          signature_logo: "",
          signature_logo_id: "00000000-0000-0000-0000-000000000000",
          signature_logo_fname: "",
          signature_logo_opt: "",
          signature_logo_opt_id: "00000000-0000-0000-0000-000000000000",
          signature_logo_opt_fname: "",
          logo: "",
          logo_fname: "",
          logo_id: "00000000-0000-0000-0000-000000000000",
          logo_opt: "",
          logo_opt_fname: "",
          logo_opt_id: "00000000-0000-0000-0000-000000000000",
          signature_name: "",
          signature_name_opt: "",
          template_name: "",
        },
        default_lang: "",
      },
      editorData: {
        showLogo2: false,
        showSignature2: false,
        create: true,
        seriesID: "",
        token: "",
        inIframe: true,
        product: "",
        libraryId: "",
        tagsLink: "",
      },
      availableCultures: [],
      supportedCultures,
    };
  },
  methods: {
    showError(text) {
      this.onError.active = true;
      this.onError.message = text;
      this.loaded = true;
    },
    setupLanguages(cultureList) {
      cultureList.forEach((item) => {
        if (this.validateCulture(item)) {
          let name = this.supportedCultures[item].name;
          this.availableCultures.push({ name, culture: item });
          this.addCultureToState(item);
        } else {
          this.showError(this.$t("error_no_culture"));
        }
      });
    },
    addCultureToState(cultureName) {
      this.seriesData.certificate_template.resources = Object.assign(
        {},
        this.seriesData.certificate_template.resources,
        {
          [cultureName]: {
            title: "",
            description: "",
            signature_title: "",
            signature_title_opt: "",
            of: this.$t("to", cultureName),
            date: this.$t("date", cultureName),
          },
        }
      );
    },
    addMissingCultures(base) {
      this.availableCultures.forEach((item) => {
        if (base.resources[item.culture] === undefined) {
          base.resources[item.culture] = {
            title: "",
            description: "",
            signature_title: "",
            signature_title_opt: "",
            of: this.$t("to", item.culture),
            date: this.$t("date", item.culture),
          };
        }
      });
    },
    validateCulture(culture) {
      if (this.supportedCultures[culture]) return true;
      return false;
    },
    loadSeries(params) {
      let vm = this;
      axios
        .get(process.env.VUE_APP_API_URL + params.seriesID, {
          headers: {
            "Content-Type": "application/json",
            authorization: params.token,
          },
        })
        .then(function (response) {
          if (response.data.template_id != 0) {
            vm.addMissingCultures(response.data.template_config);
            vm.seriesData.certificate_template = {
              ...response.data.template_config,
              ...{
                signature_logo_fname: "",
                signature_logo_opt_fname: "",
                logo_fname: "",
                logo_opt_fname: "",
              },
            };
            if (
              response.data.template_config.logo_id ===
              "00000000-0000-0000-0000-000000000000"
            ) {
              vm.seriesData.certificate_template.logo_id = createNewGUID();
            }
            if (
              vm.seriesData.certificate_template.logo_opt_id ===
              "00000000-0000-0000-0000-000000000000"
            ) {
              vm.seriesData.certificate_template.logo_opt_id = createNewGUID();
            }
            if (
              response.data.template_config.signature_logo_id ===
              "00000000-0000-0000-0000-000000000000"
            ) {
              vm.seriesData.certificate_template.signature_logo_id =
                createNewGUID();
            }
            if (
              response.data.template_config.signature_logo_opt_id ===
              "00000000-0000-0000-0000-000000000000"
            ) {
              vm.seriesData.certificate_template.signature_logo_opt_id =
                createNewGUID();
            }

            if (vm.seriesData.certificate_template.logo_opt.length > 0) {
              vm.editorData.showLogo2 = true;
            }

            if (
              vm.seriesData.certificate_template.signature_logo_opt.length >
                0 ||
              vm.seriesData.certificate_template.signature_name_opt.length >
                0 ||
              vm.seriesData.certificate_template.resources[
                vm.seriesData.default_lang
              ].signature_title_opt.length > 0
            ) {
              vm.editorData.showSignature2 = true;
            }

            vm.editorData.create = false;
          }
          vm.loaded = true;
        })
        .catch(function () {
          vm.showError(vm.$t("error_no_load"));
        });
    },
  },
  computed: {
    isModalOpen() {
      return this.modalOptions.active;
    },
  },
  components: {
    CertForm,
    CertTemplateViewer,
    CertFullPageLoader,
    CertModal,
    CertFullPageError,
    CertUploader,
  },
  created() {
    const params = getUrlParameters();
    this.setupLanguages(params.availableCultures);
    this.editorData.product = params.product;
    this.editorData.libraryId = params.libraryId;
    this.editorData.tagsLink = params.tagsLink;

    if ("token" in params) {
      this.editorData.token = params.token;
    } else {
      this.showError(this.$t("error_no_token"));
    }
    if (!this.onError.active) {
      if ("seriesID" in params) {
        this.loadSeries(params);
        this.editorData.seriesID = params.seriesID;
      } else {
        this.showError(this.$t("error_no_seriesID"));
      }
    }

    if (
      this.validateCulture(params.defaultCulture) &&
      this.validateCulture(params.uiCulture)
    ) {
      this.$i18n.locale = params.uiCulture;
      this.seriesData.default_lang = params.defaultCulture;
      this.currentCulture.name =
        this.supportedCultures[params.defaultCulture].name;
      this.currentCulture.culture = params.defaultCulture;
      this.showRtl = this.supportedCultures[params.defaultCulture].rtl;
    } else {
      this.$i18n.locale = process.env.VUE_APP_I18N_FALLBACK_LOCALE;
      this.seriesData.default_lang = process.env.VUE_APP_I18N_FALLBACK_LOCALE;
      this.currentCulture.name =
        this.supportedCultures[process.env.VUE_APP_I18N_FALLBACK_LOCALE].name;
      this.currentCulture.culture = process.env.VUE_APP_I18N_FALLBACK_LOCALE;
      this.showError(this.$t("error_no_culture"));
    }

    this.editorData.inIframe = inIframe();
  },
};
</script>

<style lang="scss">
html,
body,
#app {
  height: 100%;
  background-color: $bgMediumGray;
}

#app.modal-hidden {
  overflow-y: hidden;
}
#app.rtl {
  direction: rtl;
}

body {
  font-family: "Lato", sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

//Reset
*,
*::before,
*:after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

*:focus {
  outline: 0;
}

#app {
  .flex-container {
    display: flex;
    min-height: 100%;
  }

  .flex-container.start {
    align-items: flex-start;
  }
}

.iframe .flex-container {
  height: 100%;
}

.f-clear:after {
  content: " ";
  clear: both;
  display: block;
}

.f-left {
  float: left;
}

.f-right {
  float: right;
}

.list-inline {
  list-style: none;

  li {
    display: inline-block;
  }
}

.center {
  text-align: center;
}

.wrap {
  overflow-wrap: break-word;
  -ms-word-wrap: break-word;
  word-wrap: break-word;
  -ms-hyphens: auto;
  -moz-hyphens: auto;
  -webkit-hyphens: auto;
  hyphens: auto;
  overflow-wrap: break-word;
  white-space: normal;
}

.hidden {
  display: none;
}

.full {
  width: 100%;
  height: 100%;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}
.sr-only-focusable:active,
.sr-only-focusable:focus {
  position: static;
  width: auto;
  height: auto;
  margin: 0;
  overflow: visible;
  clip: auto;
}

.text-ellipsis {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
